import React, { useEffect, useRef } from "react";

declare global {
  interface Window {
    loadWembiiWhatsapp?: (companyId: string) => void;
  }
}

const resolveEmbeddedScriptUrl = (domain?: string) => {
  if (!domain) return null;
  const trimmed = String(domain).trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
};

interface Props {
  companyId?: string;
  includeWhatsapp?: boolean;
}

/** Loads the Wembii WhatsApp widget inside the editor canvas preview. */
export function WebsiteWidgetPreview({ companyId, includeWhatsapp }: Props) {
  const loadedRef = useRef(false);

  useEffect(() => {
    loadedRef.current = false;
    const existing = document.getElementById("wembii-whatsapp-widget");
    existing?.remove();
  }, [companyId, includeWhatsapp]);

  useEffect(() => {
    if (!companyId || !includeWhatsapp || loadedRef.current) return;

    const scriptUrl = resolveEmbeddedScriptUrl(process.env.REACT_APP_EMBEDDED_WHATSAPP_DOMAIN);
    if (!scriptUrl) return;

    const mount = () => {
      if (loadedRef.current || !window.loadWembiiWhatsapp) return;
      loadedRef.current = true;
      window.loadWembiiWhatsapp(btoa(String(companyId)));
    };

    const existing = document.querySelector(`script[data-wembii-whatsapp-loader="true"]`);
    if (existing) {
      mount();
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.dataset.wembiiWhatsappLoader = "true";
    script.onload = mount;
    document.body.appendChild(script);

    return () => {
      document.getElementById("wembii-whatsapp-widget")?.remove();
    };
  }, [companyId, includeWhatsapp]);

  return null;
}
